import React, { useRef, useEffect, useState } from 'react'
import Link from 'next/link'
import { chunk } from 'lodash'
import {
  motion,
  useMotionValue,
  animate,
  AnimationPlaybackControls,
} from 'motion/react'
import { Button } from '@mc/design-system'
import { isMobile } from '@mc/devices'
import { useTranslation } from '@mc/i18n'
import { TrackingContainer } from '@mc/visibility'
import { CATEGORY_SLUGS } from '../../constants/categories'
import { usePersonaEvents } from '../../events'
import { useNonCommerceGoldStandardEvents } from '../../hooks/tracking/useNonCommerceGoldStandardEvents'
import { ConversationStarterTopic } from '../../types/topics'
import { Locations, PageSources } from '../../types/tracking'
import { useCtaToPurchaseUrl } from '../../utils/cta'
import { TrackingSectionContainer } from '../TrackingSectionContainer'
import styles from './UseCaseSection.module.scss'

const DESKTOP_SCROLL_SPEED = 50
const MOBILE_SCROLL_SPEED = 90

const TopicItem = ({
  topic,
  index,
  rowAnalytics,
  source,
}: {
  topic: ConversationStarterTopic
  index: number
  rowAnalytics: {
    rowName: string
    rowPositionIndex: number
    pageSource?: string
  }
  source: PageSources
}) => {
  const { trackPersonaClick, trackPersonaRowTileClicked } = usePersonaEvents()
  const { trackPurchaseClick } = useNonCommerceGoldStandardEvents(source)

  return (
    <Button
      render={<Link href={topic.redirectUrl} />}
      kind='secondary'
      className={`mc-bg-neutral-900 d-flex flex-row flex-none mc-py-2 mc-px-9 ${styles.item}`}
      onClick={() => {
        if (!topic || !topic.analytics) return

        trackPersonaRowTileClicked({
          ...rowAnalytics,
          tilePositionIndex: index,
          tileTitle: topic.analytics.cta as string,
          tileSubtitle: topic.text.caption as string,
          tileLink: topic.redirectUrl,
          tileInstructorSlug: topic.analytics.instructorSlug as string,
        })
        trackPersonaClick({
          cta: 'call suggestion',
          location: 'conversation starters',
          instructorSlug: topic.analytics.instructorSlug as string,
          suggestedPrompt: topic.text.displayText,
        })
        trackPurchaseClick({
          pageSource: rowAnalytics?.pageSource || 'persona landing page',
          cta: topic.text.caption,
          location: 'conversation starters',
        })
      }}
    >
      <div className='d-flex flex-column justify-content-center'>
        <p
          className='mc-text--bold'
          style={{ fontSize: 20, lineHeight: '160%' }}
        >
          {topic.text.displayText}
        </p>
      </div>
    </Button>
  )
}

const ScrollingTopicRow = ({
  topics,
  pauseScroll,
  speedOffset,
  subRowIndex,
  rowAnalytics,
  source,
}: {
  topics: ConversationStarterTopic[]
  pauseScroll: boolean
  speedOffset: number
  subRowIndex: number
  rowAnalytics: {
    rowName: string
    rowPositionIndex: number
  }
  source: PageSources
}) => {
  const { trackPersonaRowTileViewed } = usePersonaEvents()
  const controlsRef = useRef<AnimationPlaybackControls | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const xTranslation = useMotionValue(0)

  useEffect(() => {
    if (pauseScroll) {
      controlsRef.current?.pause()
    } else {
      controlsRef.current?.play()
    }
  }, [pauseScroll])

  useEffect(() => {
    const width = containerRef.current?.scrollWidth ?? 0
    const finalPosition = -width / 2 - 8 // 8 compensates for gap which is 16px (mc-gap-4)
    const scrollSpeed = isMobile() ? MOBILE_SCROLL_SPEED : DESKTOP_SCROLL_SPEED
    const duration = width / (scrollSpeed + speedOffset * (scrollSpeed / 5))

    const controls = animate(xTranslation, [0, finalPosition], {
      ease: 'linear',
      duration,
      repeat: Infinity,
      repeatType: 'loop',
      repeatDelay: 0,
    })
    controlsRef.current = controls

    return () => {
      controls.stop()
      controlsRef.current = null
    }
  }, [xTranslation, speedOffset])

  const items = [...topics, ...topics]

  return (
    <motion.div
      ref={containerRef}
      className='d-flex mc-gap-4 mc-mb-4'
      style={{ left: 0, x: xTranslation }}
    >
      {items.map((topic, i) => (
        <TrackingContainer
          key={i}
          config={{ threshold: 0.3 }}
          className='d-flex flex-row flex-none'
          onVisible={() =>
            trackPersonaRowTileViewed({
              ...rowAnalytics,
              subrowPositionIndex: subRowIndex.toString(),
              tilePositionIndex: i.toString(),
              tileTitle: topic.text.displayText,
              tileSubtitle: topic.text.caption,
              tileInstructorSlug:
                (topic.analytics?.instructorSlug as string | undefined) || '',
              tileLink: topic.redirectUrl,
            })
          }
        >
          <TopicItem
            topic={topic}
            index={i}
            rowAnalytics={rowAnalytics}
            source={source}
          />
        </TrackingContainer>
      ))}
    </motion.div>
  )
}

type UseCaseSectionProps = {
  topics: ConversationStarterTopic[]
  ctaUrl: string
  rowAnalytics: {
    rowName: string
    rowPositionIndex: number
    pageSource?: string
  }
  source?: PageSources
  showPlansCta?: boolean
  categorySlug?: string
}

export const UseCaseSection = ({
  topics,
  ctaUrl,
  rowAnalytics,
  source = 'discovery',
  showPlansCta = true,
  categorySlug,
}: UseCaseSectionProps) => {
  const { t } = useTranslation('@mc/persona')
  const [pauseScroll, setPauseScroll] = useState(false)
  const itemsRows = chunk(topics, Math.ceil(topics.length / 3))
  const getCtaToPurchaseUrl = useCtaToPurchaseUrl()
  const { trackPersonaSignClick } = usePersonaEvents()
  const { trackPurchaseClick } = useNonCommerceGoldStandardEvents(source)

  const handleCTAClick = () => {
    const pageSource = rowAnalytics?.pageSource || 'persona landing page'
    const trackProps = {
      cta: 'sign up',
      location: rowAnalytics.rowName as Locations,
    }
    trackPersonaSignClick({
      ...trackProps,
      additionalAnalytics: { pageSource },
    })
    trackPurchaseClick({ ...trackProps, pageSource })
  }

  const title =
    categorySlug === CATEGORY_SLUGS.INTERVIEWING
      ? t('landing.useCase.interviewingTitle')
      : t('landing.useCase.title')

  return (
    <TrackingSectionContainer analytics={rowAnalytics}>
      <div className='mc-py-10 uncontainer' style={{ overflow: 'hidden' }}>
        <h2
          className={`${styles.title} mc-text-h1 mc-text--center mc-mb-10 mc-px-10 mc-px-lg-0`}
        >
          {title}
        </h2>
        <motion.div
          onHoverStart={() => setPauseScroll(true)}
          onHoverEnd={() => setPauseScroll(false)}
          onTouchStart={() => setPauseScroll(true)}
          onTouchEnd={() => setPauseScroll(false)}
        >
          {itemsRows.map((itemsRow, rowIndex) => (
            <ScrollingTopicRow
              key={rowIndex}
              topics={itemsRow}
              pauseScroll={pauseScroll}
              speedOffset={rowIndex}
              subRowIndex={rowIndex}
              rowAnalytics={rowAnalytics}
              source={source}
            />
          ))}
        </motion.div>
        {showPlansCta && (
          <div className='mc-text--center'>
            <Button
              className='mc-mt-9 mc-px-11 mc-py-4 mc-corners--12'
              render={<Link href={getCtaToPurchaseUrl(ctaUrl, source)} />}
              onClick={handleCTAClick}
            >
              {t('landing.plans.cta')}
            </Button>
          </div>
        )}
      </div>
    </TrackingSectionContainer>
  )
}
